import React, { useEffect } from 'react';
import { memo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
  changeCompanyDeviceAtom,
  changeROCAtom,
} from '../../../models/atoms/company_atoms';
import { company_controller } from '../../../controllers/company_controller';
import { useTranslation } from '../../../contexts/TranslationContext'; // Import the translation context
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { FrequenciesSettings } from './FrequenciesSettings';
import { Stack } from '@mui/material';
import { DisclaimerText, ErrorText } from '../MHM/MHMTypography';
import { CheckPermission } from '../CheckPermission';
import { ROCSettings } from './ROCSettings';
import { SettingsCard } from './SettingsCard';
import { MHMButton } from '../MHM/MHMButton';
import { ConfirmSaveModal } from './ConfirmSaveModal';
import { ChangedFrequency } from './ChangedFrequency';
import { useIsMobile } from '../../../hooks/useIsMobile';
/* eslint-disable no-useless-escape */

export const MAX_MEASUREMENTS = 72;

export const DeviceSettingsComponent = memo(function DeviceSettingsComponent(
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) {
  const { t } = useTranslation(); // Use the translation hook
  const times = useRecoilValue(changeCompanyDeviceAtom);
  const rocSettings = useRecoilValue(changeROCAtom);
  const canManageDevices = useRecoilValue(canManageDevicesSelector);
  const isMobile = useIsMobile();

  const [normalRelayError, setNormalRelayError] = useState<
    [string, { minHours: string; maxHours: string }]
  >(['', { minHours: '', maxHours: '' }]);
  const [normalReadingError, setNormalReadingError] = useState<
    [string, { minReadingFrequency: string; maxReadingFrequency: string }]
  >(['', { minReadingFrequency: '', maxReadingFrequency: '' }]);
  const [normalTotalMeasurementsError, setNormalTotalMeasurementsError] =
    useState('');
  const [warningRelayError, setWarningRelayError] = useState<
    [string, { minHours: string; maxHours: string }]
  >(['', { minHours: '', maxHours: '' }]);
  const [warningReadingError, setWarningReadingError] = useState<
    [string, { minReadingFrequency: string; maxReadingFrequency: string }]
  >(['', { minReadingFrequency: '', maxReadingFrequency: '' }]);
  const [warningTotalMeasurementsError, setWarningTotalMeasurementsError] =
    useState('');
  const [isROCThresholdError, setIsROCThresholdError] = useState(false);

  const [modal, setModal] = useState(false);
  function openModal() {
    setModal(true);
  }
  function closeModal() {
    setModal(false);
  }

  function saveSettings() {
    closeModal();
    if (times?.change_time) {
      company_controller.modify_device_settings(
        times.message_wait_time_s,
        times.measurement_sleep_time_s,
        times.warning_message_wait_time_s,
        times.warning_measurement_sleep_time_s,
      );
    }
    if (rocSettings?.change_roc) {
      company_controller.modify_company_roc_settings(
        rocSettings.roc_mode_on,
        rocSettings.roc_threshold,
      );
    }
  }

  useEffect(() => {
    if (!times) return;
    // relayFreq = wait_time / 3600
    // readingFreq = sleep_time / 60
    // total measurements = relayFreq * (60 / readingFreq)
    // total measurements = (wait_time / 3600) * (60 / (sleep_time / 60))
    // total measurements = (wait_time / 3600) * (3600 / sleep_time)
    // total measurements = wait_time / sleep_time
    const totalNormalMeasurements =
      times.message_wait_time_s / times.measurement_sleep_time_s;
    const totalWarningMeasurements =
      times.warning_message_wait_time_s /
      times.warning_measurement_sleep_time_s;

    if (totalNormalMeasurements > MAX_MEASUREMENTS) {
      setNormalTotalMeasurementsError(
        'Total measurements exceed 100! Devices are restricted to 100 measurents per transmission interval.',
      );
    } else {
      setNormalTotalMeasurementsError('');
    }
    if (totalWarningMeasurements > MAX_MEASUREMENTS) {
      setWarningTotalMeasurementsError(
        'Total measurements exceed 100! Devices are restricted to 100 measurents per transmission interval.',
      );
    } else {
      setWarningTotalMeasurementsError('');
    }
    if (
      times.message_wait_time_s < 720 ||
      times.message_wait_time_s > 72 * 60 * 60
    ) {
      setNormalRelayError([
        'Not in the allowable range hours',
        { minHours: '0.2', maxHours: '72' },
      ]);
    } else {
      setNormalRelayError(['', { minHours: '', maxHours: '' }]);
    }
    const maxReadingFrequency = Math.min(times.message_wait_time_s / 60, 720); // Dynamic upper value
    if (
      times.measurement_sleep_time_s < 300 ||
      times.measurement_sleep_time_s > maxReadingFrequency * 60
    ) {
      setNormalReadingError([
        'Not in the allowable range minutes',
        {
          minReadingFrequency: '5.0',
          maxReadingFrequency: maxReadingFrequency.toFixed(1),
        },
      ]);
    } else {
      setNormalReadingError([
        '',
        { minReadingFrequency: '', maxReadingFrequency: '' },
      ]);
    }
    if (
      times.warning_message_wait_time_s < 360 ||
      times.warning_message_wait_time_s > times.message_wait_time_s
    ) {
      setWarningRelayError([
        'Not in the allowable range hours',
        {
          minHours: '0',
          maxHours: (times.message_wait_time_s / 3600).toFixed(1),
        },
      ]);
    } else {
      setWarningRelayError(['', { minHours: '', maxHours: '' }]);
    }
    const maxWarningReadingFrequency = Math.min(
      times.measurement_sleep_time_s / 60,
      60,
    ); // Dynamic upper value
    if (
      times.warning_measurement_sleep_time_s < 300 ||
      times.warning_measurement_sleep_time_s > maxWarningReadingFrequency * 60
    ) {
      setWarningReadingError([
        'Not in the allowable range minutes',
        {
          minReadingFrequency: '5.0',
          maxReadingFrequency: maxWarningReadingFrequency.toFixed(1),
        },
      ]);
    } else {
      setWarningReadingError([
        '',
        { minReadingFrequency: '', maxReadingFrequency: '' },
      ]);
    }
  }, [times]);

  if (!times || !rocSettings) {
    return <></>;
  }

  const isSaveButtonEnabled =
    canManageDevices &&
    (times?.change_time || rocSettings?.change_roc) &&
    !normalRelayError[0] &&
    !normalReadingError[0] &&
    !normalTotalMeasurementsError &&
    !warningRelayError[0] &&
    !warningReadingError[0] &&
    !warningTotalMeasurementsError &&
    !isROCThresholdError;

  return (
    <>
      <SettingsCard
        id="frequencies"
        title={t('Frequencies')}
        sx={{ overflow: 'unset' }}
      >
        <Stack gap={2} p={2}>
          <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
            <FrequenciesSettings
              title={t('Normal Operation Mode')}
              messageTimeKey="message_wait_time_s"
              messageTimeUnit="hours"
              messageTimeError={t(...normalRelayError)}
              measurementTimeKey="measurement_sleep_time_s"
              measurementTimeUnit="minutes"
              measurementTimeError={t(...normalReadingError)}
              totalMeasurementsError={t(normalTotalMeasurementsError, {
                maxMeasurements: String(MAX_MEASUREMENTS),
              })}
              sx={{ flex: 1 }}
            />
            <FrequenciesSettings
              title={t('High Warning Mode')}
              messageTimeKey="warning_message_wait_time_s"
              messageTimeUnit="hours"
              messageTimeError={t(...warningRelayError)}
              measurementTimeKey="warning_measurement_sleep_time_s"
              measurementTimeUnit="minutes"
              measurementTimeError={t(...warningReadingError)}
              totalMeasurementsError={t(warningTotalMeasurementsError, {
                maxMeasurements: String(MAX_MEASUREMENTS),
              })}
              sx={{ flex: 1 }}
            />
          </Stack>

          <DisclaimerText fontSize="small">
            {t(
              'The device compares every reading to the defined Warning Threshold (see device pop-outs). High Warning Mode overrides both Low Power and Weather Mode (if engaged).',
            )}
          </DisclaimerText>

          <ROCSettings setROCThresholdError={setIsROCThresholdError} />

          <DisclaimerText fontSize="small">
            {t(
              "Device settings are applied to all of the devices in the company's fleet.",
            )}
          </DisclaimerText>

          {isSaveButtonEnabled && (
            <ErrorText fontSize="small">
              {t('Changes not yet saved.')}
            </ErrorText>
          )}
          <CheckPermission hasPermission={canManageDevices}>
            <MHMButton disabled={!isSaveButtonEnabled} onClick={openModal}>
              {t('Save')}
            </MHMButton>
          </CheckPermission>
        </Stack>
      </SettingsCard>

      <ConfirmSaveModal
        open={modal}
        onCancel={closeModal}
        onConfirm={saveSettings}
        message={t(
          'Are you sure you want to apply these new settings to all devices in the fleet?',
        )}
      >
        <DisclaimerText sx={{ textAlign: 'center', mt: 1, mb: 1 }}>
          {t(
            'Updated settings will be applied the next time the device transmits',
          )}
        </DisclaimerText>
        <ChangedFrequency
          title={t('Normal Operation Mode')}
          messageTimeKey="message_wait_time_s"
          messageTimeUnit="hours"
          measurementTimeKey="measurement_sleep_time_s"
          measurementTimeUnit="minutes"
        />
        <ChangedFrequency
          title={t('High Warning Mode')}
          messageTimeKey="warning_message_wait_time_s"
          messageTimeUnit="hours"
          measurementTimeKey="warning_measurement_sleep_time_s"
          measurementTimeUnit="minutes"
        />
      </ConfirmSaveModal>
    </>
  );
});
